// ExcelImportDialog.js
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Alert, 
  Snackbar,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../Configuration';
import * as XLSX from 'xlsx';

const ExcelImportDialog = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchCampuses = async () => {
    try {
      const campusSnapshot = await getDocs(collection(db, 'Campus'));
      let campusList = campusSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter campuses based on user's campusCode from localStorage
      const allowedCampus = JSON.parse(localStorage.getItem('campusCode')) || [];
      if (allowedCampus.length > 0) {
        campusList = campusList.filter(campus => allowedCampus.includes(campus.campusCode));
      }

      setCampuses(campusList);
      
      if (campusList.length === 0) {
        showSnackbar('No campuses available for your access level. Please contact administrator.', 'warning');
      }
    } catch (error) {
      console.error('Error fetching campuses:', error);
      showSnackbar('Error loading campuses. Please try again.', 'error');
    }
  };

  useEffect(() => {
    if (open) {
      fetchCampuses();
    }
  }, [open]);

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.xlsx') && !uploadedFile.name.endsWith('.xls')) {
      showSnackbar('Please upload an Excel file (.xlsx or .xls)', 'error');
      return;
    }

    setFile(uploadedFile);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          showSnackbar('Excel file must have at least a header row and one data row', 'error');
          setLoading(false);
          return;
        }

        const headers = jsonData[0];
        const rows = jsonData.slice(1);

        const processedData = rows.map((row, index) => {
          const rowData = {};
          headers.forEach((header, colIndex) => {
            if (header) {
              rowData[header.trim()] = row[colIndex] || '';
            }
          });
          rowData.rowNumber = index + 2; // Excel row number (1-based, +1 for header)
          return rowData;
        });

        setData(processedData);
        showSnackbar(`Successfully loaded ${processedData.length} students from Excel file`, 'success');
      } catch (error) {
        console.error('Error reading Excel file:', error);
        showSnackbar('Error reading Excel file. Please check the format.', 'error');
      }
      setLoading(false);
    };

    reader.readAsBinaryString(uploadedFile);
  };

  const validateStudentData = (student, rowNumber) => {
    const errors = [];
    
    if (!student.name || !student.name.trim()) {
      errors.push('Student Name is required');
    }
    if (!student.id || !student.id.trim()) {
      errors.push('Student ID is required');
    }
    if (!student.campusCode || !student.campusCode.trim()) {
      errors.push('Campus Code is required');
    }

    // Validate campus code exists
    const campusExists = campuses.some(campus => campus.campusCode === student.campusCode);
    if (!campusExists) {
      errors.push(`Campus Code "${student.campusCode}" does not exist`);
    }

    return errors;
  };

  const handleImport = async () => {
    if (data.length === 0) {
      showSnackbar('No data to import', 'error');
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const student of data) {
      const validationErrors = validateStudentData(student, student.rowNumber);
      
      if (validationErrors.length > 0) {
        errorCount++;
        errors.push(`Row ${student.rowNumber}: ${validationErrors.join(', ')}`);
        continue;
      }

      try {
        await addDoc(collection(db, 'Students'), {
          name: student.name.trim(),
          id: student.id.trim(),
          gender: student.gender || '',
          cnic: student.cnic || '',
          status: student.status || '',
          interview: student.interview || '',
          scholarship: student.scholarship || '',
          fatherName: student.fatherName || '',
          nameInUrdu: student.nameInUrdu || '',
          fatherNameInUrdu: student.fatherNameInUrdu || '',
          age: student.age || '',
          address: student.address || '',
          phoneRes: student.phoneRes || '',
          mobile: student.mobile || '',
          whatsapp: student.whatsapp || '',
          email: student.email || '',
          profession: student.profession || '',
          qualification: student.qualification || '',
          reason: student.reason || '',
          campusCode: student.campusCode.trim()
        });
        successCount++;
      } catch (error) {
        console.error('Error adding student:', error);
        errorCount++;
        errors.push(`Row ${student.rowNumber}: Database error - ${error.message}`);
      }
    }

    setImporting(false);

    if (successCount > 0) {
      showSnackbar(`Successfully imported ${successCount} students${errorCount > 0 ? `, ${errorCount} failed` : ''}`, 'success');
      if (errorCount > 0) {
        console.log('Import errors:', errors);
      }
      onClose();
    } else {
      showSnackbar(`Import failed. ${errorCount} errors occurred.`, 'error');
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      [
        'name', 'id', 'gender', 'cnic', 'status', 'interview', 'scholarship', 
        'fatherName', 'nameInUrdu', 'fatherNameInUrdu', 'age', 'address', 
        'phoneRes', 'mobile', 'whatsapp', 'email', 'profession', 'qualification', 
        'reason', 'campusCode'
      ],
      [
        'John Doe', 'ST001', 'Male', '12345-1234567-1', 'Active', 'Passed', 'No',
        'Father Name', 'جان ڈو', 'والد کا نام', '25', '123 Main St', '123-456-7890',
        '0300-1234567', '0300-1234567', 'john@example.com', 'Student', 'Matric',
        'Learning', 'QAD'
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students Template');
    
    XLSX.writeFile(wb, 'students_import_template.xlsx');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Import Students from Excel</DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              Instructions:
            </Typography>
            <Typography variant="body2" paragraph>
              1. Download the template below to see the required format
            </Typography>
            <Typography variant="body2" paragraph>
              2. Fill in your student data following the template format
            </Typography>
            <Typography variant="body2" paragraph>
              3. Upload your Excel file (.xlsx or .xls)
            </Typography>
            <Typography variant="body2" color="error">
              Required fields: name, id, campusCode
            </Typography>
          </Box>

          <Box mb={3}>
            <Button 
              variant="outlined" 
              onClick={downloadTemplate}
              sx={{ mr: 2 }}
            >
              Download Template
            </Button>
            <input
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              id="excel-file-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="excel-file-upload">
              <Button variant="contained" component="span">
                Choose Excel File
              </Button>
            </label>
            {file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>

          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}

          {data.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview ({data.length} students):
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Campus Code</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.slice(0, 10).map((student, index) => (
                      <TableRow key={index}>
                        <TableCell>{student.rowNumber}</TableCell>
                        <TableCell>{student.name}</TableCell>
                        <TableCell>{student.id}</TableCell>
                        <TableCell>{student.campusCode}</TableCell>
                        <TableCell>{student.gender}</TableCell>
                        <TableCell>{student.status}</TableCell>
                      </TableRow>
                    ))}
                    {data.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          ... and {data.length - 10} more rows
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            color="primary" 
            disabled={data.length === 0 || importing}
            startIcon={importing ? <CircularProgress size={20} /> : null}
          >
            {importing ? 'Importing...' : 'Import Students'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExcelImportDialog;
